// Enterprise brand/company profile — lendcord-core's EnterpriseBrandProfileController
// (/enterprise/brand-profile), ENTERPRISE-only server-side (EnterpriseProfileService
// .requireEnterpriseCaller). Logo upload reuses the same 3-step presigned-URL flow as
// profile documents (requestLogoUploadUrl -> PUT the file -> confirmLogo) — see
// features/profile/api/profile.api.ts's requestDocumentUploadUrl/confirmDocumentUpload
// for the reference shape this mirrors.

import { baseApi } from "@/app/store/baseApi";
import type { EnterpriseLogoUploadUrlResponse, EnterpriseProfile, EnterpriseProfileRequest } from "../setup.types";

export const brandProfileApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBrandProfile: builder.query<EnterpriseProfile, void>({
      query: () => ({ url: "/enterprise/brand-profile", auth: true }),
      providesTags: ["EnterpriseBrandProfile"],
    }),
    updateBrandProfile: builder.mutation<EnterpriseProfile, EnterpriseProfileRequest>({
      query: (body) => ({ url: "/enterprise/brand-profile", method: "PUT", body, auth: true }),
      invalidatesTags: ["EnterpriseBrandProfile"],
    }),

    // Ownership-checked, short-lived presigned download URL for the logo —
    // generated fresh per call, not bundled into GET .../brand-profile.
    // keepUnusedDataFor: 0 because the URL expires, same reasoning
    // profile.api.ts's getDocumentDownloadUrl uses.
    getLogoDownloadUrl: builder.query<{ url: string }, void>({
      query: () => ({ url: "/enterprise/brand-profile/logo/url", auth: true }),
      keepUnusedDataFor: 0,
    }),

    // Step 1 of 3 — generates a storageKey and presigned upload URL. Doesn't
    // touch the profile row yet (see confirmLogo).
    requestLogoUploadUrl: builder.mutation<EnterpriseLogoUploadUrlResponse, void>({
      query: () => ({ url: "/enterprise/brand-profile/logo/upload-url", method: "POST", auth: true }),
    }),

    // Step 3 of 3 — confirms the upload landed; this is the point the
    // profile row's logoStorageKey actually gets set.
    confirmLogo: builder.mutation<EnterpriseProfile, { storageKey: string }>({
      query: (body) => ({ url: "/enterprise/brand-profile/logo", method: "POST", body, auth: true }),
      invalidatesTags: ["EnterpriseBrandProfile"],
    }),
  }),
});

export const {
  useGetBrandProfileQuery,
  useUpdateBrandProfileMutation,
  useLazyGetLogoDownloadUrlQuery,
  useRequestLogoUploadUrlMutation,
  useConfirmLogoMutation,
} = brandProfileApi;

// Step 2 of 3 — PUT the raw logo bytes to the presigned URL from step 1.
// Deliberately NOT an RTK Query endpoint, same reasoning profile.api.ts's
// uploadDocumentFile: the signature embedded in the URL is the auth, this
// isn't a request to our own backend for baseApi's tenant/auth conventions
// to apply to. Reuses the same dev-proxy rewrite for local-document-storage
// URLs — see that function's own comment for why.
function toProxiedUploadUrl(uploadUrl: string): string {
  if (process.env.API_URL) {
    return uploadUrl;
  }
  const parsed = new URL(uploadUrl);
  return parsed.pathname.startsWith("/internal/local-storage") ? `${parsed.pathname}${parsed.search}` : uploadUrl;
}

export async function uploadLogoFile(uploadUrl: string, file: File): Promise<void> {
  const response = await fetch(toProxiedUploadUrl(uploadUrl), {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!response.ok) {
    throw new Error(`Logo upload failed with status ${response.status}`);
  }
}
